/**
 * Toasts (idc-portal-design v1.1): a bottom-right stack of at most 3, one toast per operation (busy → ok / err,
 * updated in place). ok/info dismiss after 5s; busy toasts never do; an error that carries a recovery action stays
 * until dismissed or acted on; a plain error goes after 8s. Hover and focus pause the timer (WCAG 2.2.1). The stack is
 * a plain region: the outcome is announced through separate hidden polite / assertive live nodes.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, type PortalApi } from "../api/client.ts";
import { usePortal } from "../api/hooks.ts";
import { prefersReducedMotion } from "./motion.ts";
import { Icon } from "./Sprites.tsx";

export type ToastTone = "busy" | "ok" | "err" | "info";
export interface ToastInput {
  readonly tone: ToastTone;
  readonly title: string;
  readonly desc?: string;
  readonly action?: { readonly label: string; readonly onAction: () => void };
  readonly duration?: number;
}
export interface ToastHandle { update(next: ToastInput): void; close(): void }
export interface ToastApi { show(t: ToastInput): ToastHandle }

export const MAX_TOASTS = 3;
const ICON: Readonly<Record<ToastTone, string>> = { busy: "mail", ok: "ok", err: "alert", info: "info" };
const OUT_MS = 160;

/** How long a toast stays; null = until dismissed. */
export function toastDuration(t: ToastInput): number | null {
  if (t.tone === "busy") return null;
  if (t.tone === "err") return t.action ? null : (t.duration ?? 8000);
  return t.duration ?? 5000;
}

interface Item { id: number; input: ToastInput; out: boolean }
interface Timer { handle: ReturnType<typeof setTimeout> | null; remaining: number; started: number }

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const v = useContext(ToastContext);
  if (!v) throw new Error("useToast outside ToastProvider");
  return v;
}

/** An API call's outcome, for a toast-reported action. */
export type Outcome<T> = { ok: true; value: T } | { ok: false; error: ApiError | Error };

/**
 * Runs an API call for a toast-reported action (shared by the screens, AIC-182): the error comes back for the toast
 * rather than being thrown, and a 401 ends the session.
 */
export function useAttempt() {
  const { api, expire } = usePortal();
  return async <T,>(fn: (api: PortalApi) => Promise<T>): Promise<Outcome<T>> => {
    try {
      return { ok: true, value: await fn(api) };
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) expire();
      return { ok: false, error: e instanceof Error ? e : new Error(String(e)) };
    }
  };
}

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const [polite, setPolite] = useState("");
  const [assertive, setAssertive] = useState("");
  const timers = useRef(new Map<number, Timer>());
  const nextId = useRef(1);

  const announce = useCallback((t: ToastInput) => {
    const text = t.desc ? `${t.title}${/[.!?…]$/.test(t.title.trim()) ? "" : "."} ${t.desc}` : t.title;
    const set = t.tone === "err" ? setAssertive : setPolite;
    set("");
    setTimeout(() => set(text), 40); // clear, then set, so a repeat is announced
  }, []);

  const clearTimer = (id: number) => {
    const t = timers.current.get(id);
    if (t?.handle) clearTimeout(t.handle);
    timers.current.delete(id);
  };

  const remove = useCallback((id: number) => {
    clearTimer(id);
    if (prefersReducedMotion()) {
      setItems((xs) => xs.filter((x) => x.id !== id));
      return;
    }
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, out: true } : x)));
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), OUT_MS);
  }, []);

  const arm = useCallback((id: number, ms: number | null) => {
    clearTimer(id);
    if (ms === null) return;
    timers.current.set(id, { handle: setTimeout(() => remove(id), ms), remaining: ms, started: Date.now() });
  }, [remove]);

  const pause = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (!t?.handle) return;
    clearTimeout(t.handle);
    timers.current.set(id, { handle: null, remaining: Math.max(0, t.remaining - (Date.now() - t.started)), started: 0 });
  }, []);

  const resume = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (!t || t.handle) return;
    const ms = Math.max(1500, t.remaining);
    timers.current.set(id, { handle: setTimeout(() => remove(id), ms), remaining: ms, started: Date.now() });
  }, [remove]);

  useEffect(() => () => { for (const id of [...timers.current.keys()]) clearTimer(id); }, []);

  const api = useMemo<ToastApi>(() => ({
    show(input) {
      const id = nextId.current++;
      setItems((xs) => {
        const live = xs.filter((x) => !x.out);
        const drop = live.length >= MAX_TOASTS ? live.slice(0, live.length - MAX_TOASTS + 1).map((x) => x.id) : [];
        for (const d of drop) clearTimer(d);
        return [...xs.filter((x) => !drop.includes(x.id)), { id, input, out: false }];
      });
      arm(id, toastDuration(input));
      announce(input);
      return {
        update(next) {
          setItems((xs) => xs.map((x) => (x.id === id ? { ...x, input: next } : x)));
          arm(id, toastDuration(next));
          announce(next);
        },
        close: () => remove(id),
      };
    },
  }), [announce, arm, remove]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="ipd-toasts" role="region" aria-label="Notifications">
        {items.map((x) => (
          <div
            key={x.id}
            className={`ipd-toast${x.out ? " is-out" : ""}`}
            data-tone={x.input.tone}
            onMouseEnter={() => pause(x.id)}
            onMouseLeave={() => resume(x.id)}
            onFocus={() => pause(x.id)}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) resume(x.id); }}
          >
            <Icon name={ICON[x.input.tone]} />
            <div>
              <b>{x.input.title}</b>
              {x.input.desc ? <p>{x.input.desc}</p> : null}
              {x.input.action ? (
                <div className="ipd-toast-acts">
                  <button type="button" className="ipd-link" onClick={() => { const a = x.input.action!; remove(x.id); a.onAction(); }}>{x.input.action.label}</button>
                </div>
              ) : null}
            </div>
            <button type="button" className="ipd-toast-x" aria-label="Dismiss notification" onClick={() => remove(x.id)}><Icon name="x" /></button>
          </div>
        ))}
      </div>
      {/* aria-live rather than role=status/alert: same announcement, without adding permanent (empty) status and
          alert landmarks to every screen. */}
      <p className="ipd-sr" aria-live="polite" aria-atomic="true" data-testid="toast-status">{polite}</p>
      <p className="ipd-sr" aria-live="assertive" aria-atomic="true" data-testid="toast-alert">{assertive}</p>
    </ToastContext.Provider>
  );
}
